import React, { useEffect, useState } from 'react';
import { Star, Loader2, Check, X, Trash2, MessageSquare, Plus, Search } from 'lucide-react';
import api from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const BLANK_ADD = { name: '', user_id: null, rating: 5, text: '', program_title: '', source_note: '', is_featured: false };

const AdminTestimonials = () => {
  const { toast } = useToast();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(BLANK_ADD);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = async () => {
    setLoading(true);
    try {
      if (tab === 'feedback') {
        const res = await api.get('/admin/feedback');
        setFeedback(res.data || []);
      } else {
        const res = await api.get('/admin/testimonials', { params: { status: tab } });
        setItems(res.data || []);
      }
    } catch (e) { console.error('Load failed:', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const moderate = async (t, body, label) => {
    setBusy(t.id);
    try { await api.patch(`/admin/testimonials/${t.id}`, body); toast({ title: label }); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setBusy(null); }
  };
  const remove = async (t) => {
    if (!window.confirm('Delete this testimonial permanently?')) return;
    setBusy(t.id);
    try { await api.delete(`/admin/testimonials/${t.id}`); toast({ title: 'Deleted' }); load(); }
    catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail }); }
    finally { setBusy(null); }
  };

  const runSearch = async () => {
    if (!search.trim()) { setResults([]); return; }
    setSearching(true);
    try { const res = await api.get('/admin/users', { params: { q: search.trim() } }); setResults(res.data || []); }
    catch (e) { console.error('Search failed:', e); }
    finally { setSearching(false); }
  };
  const linkCustomer = (u) => { setF('user_id', u.user_id); setF('name', u.full_name); setResults([]); setSearch(u.full_name); };
  const openAdd = () => { setForm(BLANK_ADD); setSearch(''); setResults([]); setShowAdd(true); };
  const submitAdd = async () => {
    if (!form.name.trim()) return toast({ title: 'Add a customer name or link a customer' });
    if (form.text.trim().length < 10) return toast({ title: 'Testimonial is too short (min 10 characters)' });
    setSaving(true);
    try {
      await api.post('/admin/testimonials', {
        name: form.user_id ? undefined : form.name.trim(),
        user_id: form.user_id || undefined,
        rating: Number(form.rating),
        text: form.text.trim(),
        program_title: form.program_title.trim() || undefined,
        source_note: form.source_note.trim() || undefined,
        is_featured: form.is_featured,
      });
      toast({ title: 'Testimonial published', description: form.name });
      setShowAdd(false);
      setTab('published');
      load();
    } catch (e) { toast({ title: 'Failed', description: e?.response?.data?.detail, variant: 'destructive' }); }
    finally { setSaving(false); }
  };

  const tabs = [['pending', 'Pending'], ['published', 'Published'], ['rejected', 'Rejected'], ['feedback', 'Feedback']];

  return (
    <div className="space-y-6" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Admin</div>
          <h1 className="font-serif-display text-4xl text-burgundy">Testimonials & Feedback</h1>
        </div>
        <button onClick={openAdd} data-testid="add-testimonial-btn" className="pill-btn">
          <Plus className="w-4 h-4" /> Add testimonial
        </button>
      </div>

      <div className="flex items-center gap-2 border-b border-rose/40 flex-wrap">
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`testimonials-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${tab === k ? 'border-burgundy text-burgundy font-medium' : 'border-transparent text-mahogany/60 hover:text-mahogany'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-mahogany"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : tab === 'feedback' ? (
        <div className="space-y-3" data-testid="feedback-list">
          {feedback.length === 0 && <div className="text-mahogany/60 text-sm">No feedback yet.</div>}
          {feedback.map((f) => (
            <div key={f.id} className="p-4 rounded-2xl border border-rose/40 bg-white/70">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-burgundy font-medium">
                  {f.is_anonymous ? 'Anonymous' : (f.user_name || 'User')}
                  {!f.is_anonymous && f.user_email && <span className="text-xs text-mahogany/60 ml-2">{f.user_email}</span>}
                </span>
                {f.rating && <span className="flex items-center gap-0.5">{Array.from({ length: f.rating }).map((_, s) => <Star key={s} className="w-3.5 h-3.5" fill="#A44758" stroke="#A44758" />)}</span>}
              </div>
              <p className="text-sm text-mahogany/90">{f.text}</p>
              <div className="text-[11px] text-mahogany/50 mt-1">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3" data-testid="testimonials-list">
          {items.length === 0 && <div className="text-mahogany/60 text-sm">No {tab} testimonials.</div>}
          {items.map((t) => (
            <div key={t.id} className="p-4 rounded-2xl border border-rose/40 bg-white/70" data-testid={`testimonial-row-${t.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-burgundy font-medium">{t.name}</span>
                    <span className="flex items-center gap-0.5">{Array.from({ length: t.rating }).map((_, s) => <Star key={s} className="w-3.5 h-3.5" fill="#A44758" stroke="#A44758" />)}</span>
                    {t.is_featured && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-rose-soft text-mahogany">Featured</span>}
                  </div>
                  {t.program && <div className="text-xs text-mahogany/60 mb-1">{t.program}</div>}
                  <p className="text-sm text-burgundy/90">"{t.text}"</p>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  {t.status !== 'published' && (
                    <button onClick={() => moderate(t, { status: 'published' }, 'Published')} disabled={busy === t.id}
                      data-testid={`publish-${t.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-100 text-green-800 hover:bg-green-200 transition-colors">
                      <Check className="w-3.5 h-3.5" /> Publish
                    </button>
                  )}
                  {t.status === 'published' && (
                    <button onClick={() => moderate(t, { is_featured: !t.is_featured }, t.is_featured ? 'Unfeatured' : 'Featured')} disabled={busy === t.id}
                      data-testid={`feature-${t.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-rose-soft text-mahogany hover:bg-rose-deep transition-colors">
                      <Star className="w-3.5 h-3.5" /> {t.is_featured ? 'Unfeature' : 'Feature'}
                    </button>
                  )}
                  {t.status !== 'rejected' && (
                    <button onClick={() => moderate(t, { status: 'rejected' }, 'Rejected')} disabled={busy === t.id}
                      data-testid={`reject-${t.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">
                      <X className="w-3.5 h-3.5" /> Reject
                    </button>
                  )}
                  <button onClick={() => remove(t)} disabled={busy === t.id}
                    data-testid={`delete-${t.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto" data-testid="add-testimonial-modal">
          <div className="bg-cream rounded-3xl border border-rose/50 shadow-xl max-w-lg w-full p-6 space-y-4 my-8">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-2xl text-burgundy">Add a testimonial</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-mahogany/70" /></button>
            </div>
            <p className="text-xs text-mahogany/70">Type a customer's testimonial (e.g. transcribed from a video or email they sent). It publishes to the homepage carousel and testimonials page right away.</p>

            {/* Link a customer */}
            <div>
              <div className="text-xs uppercase tracking-widest text-mahogany/70 mb-1">Link a customer (optional)</div>
              <div className="flex items-center bg-white rounded-full border border-rose/50 px-3 py-1.5 gap-2">
                <Search className="w-4 h-4 text-mahogany/60" />
                <input value={search} onChange={(e) => { setSearch(e.target.value); setF('user_id', null); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
                  placeholder="Search name or email…" data-testid="testimonial-customer-search"
                  className="bg-transparent focus:outline-none text-burgundy flex-1" />
                <button onClick={runSearch} className="text-xs text-mahogany hover:text-burgundy">{searching ? '…' : 'Search'}</button>
              </div>
              {form.user_id && <div className="text-xs text-green-700 mt-1" data-testid="testimonial-linked">Linked to {form.name}</div>}
              {results.length > 0 && (
                <div className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-rose/40 bg-white divide-y divide-rose/20">
                  {results.map((u) => (
                    <button key={u.user_id} onClick={() => linkCustomer(u)} data-testid={`testimonial-cust-${u.user_id}`}
                      className="block w-full text-left px-3 py-2 text-sm hover:bg-rose-soft/40">
                      <span className="text-burgundy font-medium">{u.full_name}</span> <span className="text-mahogany/60 text-xs">{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-mahogany/70">Name {form.user_id ? '(linked)' : '*'}</span>
              <input value={form.name} onChange={(e) => setF('name', e.target.value)} disabled={!!form.user_id}
                data-testid="testimonial-name-input" placeholder="Customer name"
                className="mt-1 w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany disabled:opacity-60" />
            </label>

            <div>
              <span className="text-xs uppercase tracking-widest text-mahogany/70">Rating</span>
              <div className="flex items-center gap-1 mt-1" data-testid="testimonial-rating">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setF('rating', n)} data-testid={`rating-star-${n}`}>
                    <Star className="w-6 h-6" fill={n <= form.rating ? '#A44758' : 'none'} stroke="#A44758" />
                  </button>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-xs uppercase tracking-widest text-mahogany/70">Testimonial *</span>
              <textarea value={form.text} onChange={(e) => setF('text', e.target.value)} rows={4} maxLength={1500}
                data-testid="testimonial-text-input" placeholder="Type the customer's words…"
                className="mt-1 w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
            </label>

            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-mahogany/70">Program / Event (optional)</span>
                <input value={form.program_title} onChange={(e) => setF('program_title', e.target.value)}
                  data-testid="testimonial-program-input" className="mt-1 w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              </label>
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-mahogany/70">Source note (internal)</span>
                <input value={form.source_note} onChange={(e) => setF('source_note', e.target.value)}
                  placeholder="e.g. Transcribed from video" data-testid="testimonial-source-input"
                  className="mt-1 w-full bg-white rounded-xl border border-rose/50 px-3 py-2 text-burgundy focus:outline-none focus:border-mahogany" />
              </label>
            </div>

            <label className="flex items-center gap-2 text-sm text-burgundy">
              <input type="checkbox" checked={form.is_featured} onChange={(e) => setF('is_featured', e.target.checked)} data-testid="testimonial-feature-toggle" />
              Feature this on the homepage
            </label>

            <div className="flex justify-end gap-3 pt-1">
              <button onClick={() => setShowAdd(false)} data-testid="testimonial-add-cancel"
                className="text-sm px-4 py-2 rounded-full bg-cream-2 text-mahogany hover:bg-rose-soft transition-colors">Cancel</button>
              <button onClick={submitAdd} disabled={saving} data-testid="testimonial-add-submit"
                className="text-sm px-4 py-2 rounded-full bg-burgundy text-cream hover:bg-mahogany transition-colors disabled:opacity-60 inline-flex items-center gap-2">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Publish testimonial
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTestimonials;
